import React from "react";

import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetPortfolioPlans from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlans";

import Message from "@src/sharedComponents/Message/Message";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import { TableSortOrder } from "@src/sharedComponents/Table/Types";

import {
  tableColumnsCreator,
  TableKey,
  tableRowDataCreator,
} from "./PortfolioData";

type PortfolioPlanTableProps = Pick<
  ReturnType<typeof useGetPortfolioPlans>,
  "data" | "isLoading" | "hasError"
> & {
  onPageChange: (page: number) => void;
  onSorterChange: (sortBy: TableKey, sortOrder: TableSortOrder) => void;
  orderBy: [TableKey, TableSortOrder] | null;
};

export const PortfolioPlanTable = ({
  isLoading,
  data,
  hasError,
  onPageChange,
  onSorterChange,
  orderBy,
}: PortfolioPlanTableProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const pagination = data?.pagination ? data?.pagination : undefined;

  const prevPage = (currentPage: number) =>
    currentPage - 1 > 0 ? currentPage - 1 : undefined;

  const nextPage = (currentPage: number) =>
    pagination !== undefined && currentPage + 1 <= pagination.pageCount
      ? currentPage + 1
      : undefined;

  return (
    <>
      {hasError ? (
        <Message text="Something went wrong, please try again later" />
      ) : data?.data?.length === 0 && !isLoading ? (
        <Message text="No results found" />
      ) : (
        <PaginatedTable
          isLoading={isLoading}
          columns={tableColumnsCreator(orderBy)}
          rows={tableRowDataCreator(navigate, location, data)}
          pagination={
            !pagination
              ? undefined
              : {
                  ...pagination,
                  totalPages: pagination.pageCount,
                  nextPage: nextPage(pagination.currentPage),
                  prevPage: prevPage(pagination.currentPage),
                }
          }
          onPageChange={onPageChange}
          showEmptyRows={true}
          onColumnSelect={onSorterChange}
        />
      )}
    </>
  );
};
